import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import VideoTile from './VideoTile';
import { VideoCallState } from './VideoCallManager';
import './video.css';

interface VideoCallPanelProps {
  variant: 'therapist' | 'patient';
  state: VideoCallState;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  /**
   * Patient-only: hide the video the patient SEES during a BLS set so they can
   * focus on the moving light (and to keep the animation smooth). The patient's
   * camera keeps capturing, so the therapist still observes the client.
   */
  hiddenForFocus?: boolean;
  /**
   * When provided, the panel shows a close button (and its header becomes a
   * drag handle). Used by the therapist to turn video off directly from the
   * floating panel, so disabling never depends on reaching the control card
   * underneath it.
   */
  onClose?: () => void;
}

const remoteLabelKey = {
  therapist: 'Client',
  patient: 'Therapist',
} as const;

/**
 * Floating 1:1 video panel overlaid on the session. Always additive: if the
 * call is declined/failed it shows a small non-blocking status, never an error
 * that could disrupt the BLS session (RFC-0001 §6 / ADR-0014).
 */
const VideoCallPanel: React.FC<VideoCallPanelProps> = ({
  variant,
  state,
  localStream,
  remoteStream,
  hiddenForFocus = false,
  onClose,
}) => {
  const { t } = useTranslation();
  // Drag offset from the default (bottom-right) position, so the therapist can
  // move the panel off any control it happens to overlap.
  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragStart = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);

  const startDrag = (e: React.PointerEvent) => {
    // Ignore drags that start on the close button (it has its own handler).
    if ((e.target as HTMLElement).closest('.video-panel-close')) return;
    dragStart.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y };
    const move = (ev: PointerEvent) => {
      if (!dragStart.current) return;
      setOffset({
        x: dragStart.current.ox + (ev.clientX - dragStart.current.px),
        y: dragStart.current.oy + (ev.clientY - dragStart.current.py),
      });
    };
    const up = () => {
      dragStart.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // No active call (therapist hasn't started video, or it ended) → render
  // nothing, so the session UI is unchanged when video is off.
  if (state === 'idle' || state === 'closed') {
    return null;
  }

  // During a BLS set the patient sees only the light bar.
  if (hiddenForFocus) {
    return (
      <div className="video-panel video-panel-focus" aria-hidden="true">
        <span className="video-focus-note">{t('Video paused during movement')}</span>
      </div>
    );
  }

  if (state === 'declined') {
    return (
      <div className="video-panel video-panel-status">
        <span>{t('Camera/microphone unavailable — continuing without video')}</span>
        {onClose && (
          <button
            type="button"
            className="video-panel-close video-panel-close-inline"
            onClick={onClose}
            aria-label={t('End video call')}
          >
            ✕
          </button>
        )}
      </div>
    );
  }

  const style =
    offset.x || offset.y ? { transform: `translate(${offset.x}px, ${offset.y}px)` } : undefined;

  return (
    <div className={'video-panel video-panel-' + variant} style={style}>
      <div
        className={'video-panel-bar' + (onClose ? '' : ' video-panel-bar-static')}
        onPointerDown={onClose ? startDrag : undefined}
      >
        {onClose && (
          <span className="video-panel-grip" aria-hidden="true" title={t('Drag to move')}>
            <span />
            <span />
            <span />
          </span>
        )}
        {onClose && (
          <button
            type="button"
            className="video-panel-close"
            onClick={onClose}
            aria-label={t('End video call')}
          >
            ✕
          </button>
        )}
      </div>
      <VideoTile
        stream={remoteStream}
        label={t(remoteLabelKey[variant])}
        className="video-remote"
      />
      <VideoTile
        stream={localStream}
        muted
        label={t('You')}
        className="video-local"
      />
      {state !== 'connected' && (
        <span className="video-panel-hint">
          {state === 'connecting'
            ? t('Connecting video…')
            : state === 'failed'
            ? t('Video could not connect')
            : ''}
        </span>
      )}
    </div>
  );
};

export default VideoCallPanel;
